import type { NextPageWithLayout, TypeSeo } from '@/types';
import type { InferGetStaticPropsType } from 'next';
import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { scroller } from 'react-scroll';
import HomeLayout from '@/components/layouts/_home';
import Seo from '@/components/seo/seo';
import ProductListJsonLd from '@/components/seo/product-list-json-ld';
import {
  HOME_H1,
  HOME_META_DESCRIPTION,
  HOME_META_TITLE,
  typeMetaDescription,
} from '@/lib/seo/site-copy';
import { useWindowSize } from '@/lib/use-window-size';
import { getStaticPaths, getStaticProps } from '@/framework/home-pages.ssr';
import { useType } from '@/framework/type';
import { searchDrawerAtom } from '@/store/searchDrawerAtom';
import { useModalAction } from '@/components/ui/modal/modal.context';

export { getStaticPaths, getStaticProps };

const ManagedDrawer = dynamic(
  () => import('@/components/ui/drawer/managed-drawer'),
  {
    ssr: false,
  },
);
const CartCounterButton = dynamic(
  () => import('@/components/cart/cart-counter-button'),
  { ssr: false },
);
const Classic = dynamic(() => import('@/components/layouts/classic'));
const Standard = dynamic(() => import('@/components/layouts/standard'));
const Modern = dynamic(() => import('@/components/layouts/modern'));
const Minimal = dynamic(() => import('@/components/layouts/minimal'));
const Compact = dynamic(() => import('@/components/layouts/compact'));

const MAP_LAYOUT_TO_GROUP: Record<string, any> = {
  classic: Classic,
  modern: Modern,
  standard: Standard,
  minimal: Minimal,
  compact: Compact,
  default: Classic,
};
const Home: NextPageWithLayout<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ variables, layout, isRootPage = false, typeSeo }) => {
  const { query } = useRouter();
  const { width } = useWindowSize();
  const { type } = useType(variables.types.type);
  const { openModal } = useModalAction();
  const resolvedType: TypeSeo | undefined = typeSeo ??
    (type?.name && type?.slug
      ? {
        id: type.id,
        name: type.name,
        slug: type.slug,
        banners: type.banners,
      }
      : undefined);

  useEffect(() => {
    if (query.text || query.category) {
      scroller.scrollTo('grid', {
        smooth: true,
        offset: -110,
      });
    }
  }, [query.text, query.category]);

  useEffect(() => {
    if (query.open_register === 'true') {
      // Add a small delay to ensure consent popup check completes first
      const timer = setTimeout(() => {
        openModal('REGISTER');
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [query.open_register, openModal]);

  const Component = MAP_LAYOUT_TO_GROUP[layout];
  const heading = isRootPage || !resolvedType?.name ? HOME_H1 : resolvedType.name;
  const description =
    isRootPage || !resolvedType?.name
      ? HOME_META_DESCRIPTION
      : typeMetaDescription(resolvedType.name);
  const canonicalPath =
    isRootPage || !resolvedType?.slug
      ? '/'
      : resolvedType.id != null
        ? `${resolvedType.slug}/${resolvedType.id}`
        : String(resolvedType.slug);

  // Types without a categories-products grid still SSR dynamic shelves
  // (e.g. /clinica-da-pele/28/). Prefer grid hits for ItemList; fall back to shelves.
  const ssrGridProducts = variables.ssrProducts ?? [];
  const ssrListingProducts =
    ssrGridProducts.length > 0
      ? ssrGridProducts
      : (variables.ssrShelfProducts ?? []).flat();
  const typeHasSsrProducts = ssrListingProducts.length > 0;

  return (
    <>
      <Seo
        title={
          isRootPage || !resolvedType?.name
            ? HOME_META_TITLE
            : `${resolvedType.name} | Qualidoc Farmácia Online 24 horas`
        }
        description={description}
        url={canonicalPath}
        images={resolvedType?.banners}
        noindex={!isRootPage && !typeHasSsrProducts}
        nofollow={!isRootPage && !typeHasSsrProducts}
      />
      {!isRootPage && resolvedType?.name && typeHasSsrProducts ? (
        <ProductListJsonLd
          name={resolvedType.name}
          path={canonicalPath}
          products={ssrListingProducts}
        />
      ) : null}
      <h1 className="sr-only">{heading}</h1>
      <Component variables={variables} />
      {!['minimal'].includes(layout) && width > 1023 && <CartCounterButton />}
    </>
  );
};

Home.getLayout = function getLayout(page) {
  return (
    <>
      <HomeLayout layout={page.props.layout}>{page}</HomeLayout>
      <ManagedDrawer atom={searchDrawerAtom} />
    </>
  );
};

export default Home;
